import { useForm } from "react-hook-form";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { T, useT } from "@repo/transifex";
import { Button, Dialog, DialogSuccess, type DialogProps } from "~/components";
import { EMAIL_REGEX } from "~/utils/constants";
import { cn } from "~/lib/utils";

type DialogEmailReceiptCombinedProps = Partial<DialogProps> & {
  onClose: () => void;
  onSubmit: (email: string) => void;
  status: "pending" | "success" | "error" | "idle";
};

type FormData = { email: string };

export const DialogSendReportEmail = ({
  onClose,
  onSubmit,
  status,
  ...dialogProps
}: DialogEmailReceiptCombinedProps) => {
  const t = useT();

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors }
  } = useForm<FormData>();

  if (status === "success") {
    return (
      <DialogSuccess
        {...dialogProps}
        onOutsideClick={() => {
          onClose();
          resetForm();
        }}
        title="Email sent!"
      />
    );
  }

  const isEmailInvalid = Boolean(errors.email);

  return (
    <Dialog size="md" {...dialogProps}>
      <form
        className="flex flex-col items-center justify-center px-8 py-5 text-center"
        onSubmit={handleSubmit(({ email }) => {
          onSubmit(email);
        })}
        noValidate
      >
        <div className="mb-8 flex flex-col gap-3 text-text-secondary">
          <h2 className="text-xl">
            <T _str="Send report" />
          </h2>
        </div>

        <div
          className={cn(
            "relative flex w-full flex-col rounded-md border border-gray-300 px-3 py-2 text-left",
            {
              "border-interface-negative-2": isEmailInvalid
            }
          )}
        >
          <label className="text-xs text-text-disabled">
            <T _str="Email" />
          </label>

          <input
            className="border-none text-sm text-text-secondary outline-none"
            type="email"
            {...register("email", {
              required: t("Email is required"),
              pattern: {
                value: EMAIL_REGEX,
                message: t("Invalid email address")
              }
            })}
          />

          {errors.email ? (
            <div className="mt-2 text-xs text-red-600">
              {errors.email.message}
            </div>
          ) : null}

          <FontAwesomeIcon
            className="absolute right-3 top-1/3"
            icon={faEnvelope}
          />
        </div>

        {status === "error" && (
          <div>
            <T _str="Error sending the email, try again" />
          </div>
        )}

        <div className="mt-10 flex w-full gap-3">
          <Button
            onClick={() => {
              onClose();
            }}
            size="large"
            variant="danger"
            type="button"
          >
            <T _str="Cancel" />
          </Button>

          <Button
            className="w-full"
            disabled={status === "pending" || isEmailInvalid}
            loading={status === "pending"}
            type="submit"
            size="large"
            variant="primary"
          >
            <T _str="Send" />
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
